import type { CardProps } from '@mui/material/Card';
import Card from '@mui/material/Card';
import { useTheme, alpha as hexAlpha } from '@mui/material/styles';
import CardHeader from '@mui/material/CardHeader';
import { Chart, useChart } from 'src/components/chart';
import { fShortenNumber, fShortenAndRoundNumber } from 'src/utils/format-number';
import moment from 'moment';
import Stack from '@mui/material/Stack';
import { Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { Iconify } from 'src/components/iconify';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { f } from 'src/utils/format';
import { useTranslate } from 'react-admin';
import { useEffect, useRef, useState } from 'react';
import { scaleSymlog } from 'd3-scale';
import * as chartService from '../../services/chartService';

type Props = CardProps & {
  titleRevenue?: string;
  titleProfit?: string;
  tooltipRevenue?: string;
  tooltipProfit?: string;
  subheader?: string;
  chart: {
    colors?: string[];
    categories?: string[];
    series: {
      name?: string;
      data: number[];
    }[];
  };
};

export function RevenueChart({
  titleRevenue,
  titleProfit,
  tooltipRevenue,
  tooltipProfit,
  subheader,
  chart,
  ...other
}: Props) {
  const chartRef = useRef(null);
  const theme = useTheme();
  const [showLogScale, setShowLogScale] = useState(false);
  const translate = useTranslate();

  const handleToggleProfit = () => {
    setShowLogScale((prev) => !prev);
  };

  const chartColors = chart.colors ?? [
    theme.palette.success.dark,
    hexAlpha(theme.palette.error.main, 0.48),
  ];

  const symlog = scaleSymlog().domain([0.01, 1000]).range([0, 100]);

  const scaledRevenue = chart.series[0].data.map((y) => symlog(y));
  const scaledProfit = chart.series[1].data.map((y) => symlog(y));

  const series = [
    { name: chart.series[0].name, data: scaledRevenue },
    { name: chart.series[1].name, data: scaledProfit },
  ];

  const filteredSeries = showLogScale ? series : chart.series;

  const hasNegativeProfit = Math.min(...chart.series[1].data) < 0;

  const m_categories = chart?.categories || [];

  const logScaleChartOptions = useChart({
    colors: chartColors,
    legend: { show: true },
    chart: {
      events: {
        legendClick: (chartContext, seriesIndex, opts) => {
          console.log({ chartContext, seriesIndex, opts });
          const containerElement = document.getElementById('revenue-chart-container');
          if (containerElement) {
            if (seriesIndex === 0) {
              if (containerElement.style.paddingLeft === '40px')
                containerElement.style.paddingLeft = '8px';
              else containerElement.style.paddingLeft = '40px';
            }
          }
        },
      },
    },
    xaxis: {
      categories: chart.categories,
      labels: {
        formatter: (value, timestamp: number, opts: number) => {
          if (m_categories.length > 30) {
            const index = (chart.categories || []).indexOf(value);
            return index % 7 === 0 ? moment(new Date(value).toString()).format('DD-MM-YY') : '';
          }
          return moment(new Date(value).toString()).format('DD-MM-YY');
        },
        rotate: -45,
        rotateAlways: true,
      },
    },
    yaxis: [
      {
        title: {
          text: `(USD)${translate('module.dashBoard.revenue')}`,
          style: { color: chartColors[0] },
        },
        labels: {
          formatter: (value: number) => fShortenAndRoundNumber(Math.round(symlog.invert(value))),
          style: {
            colors: chartColors[0],
          },
        },
        min: 0,
      },
      {
        opposite: true,
        title: {
          text: `${translate('module.dashBoard.profitOverTime')}(USD)`,
          style: { color: chartColors[1] },
        },
        labels: {
          formatter: (value: number) => fShortenAndRoundNumber(Math.round(symlog.invert(value))),
          style: {
            colors: chartColors[1],
          },
          offsetX: -10,
        },

        min: (min) => {
          if (min >= 0) return 0;
          // eslint-disable-next-line prefer-exponentiation-operator, no-restricted-properties
          const magnitude = Math.pow(10, Math.floor(Math.log10(-min)));
          const multiple = -min / magnitude; // example : 1.22
          const offset = multiple - Math.floor(multiple); // example : 0.22
          const buffer = offset < 0.5 ? 0.5 : 0;
          const bufferMagnitude = buffer * magnitude;
          const result = -(Math.round(multiple) * magnitude + bufferMagnitude);
          return result * (m_categories.length > 15 ? 1.5 : 1);
        },
      },
    ],
    annotations: {
      yaxis: [
        ...(hasNegativeProfit ? [{ y: symlog(0), borderColor: 'gray', strokeDashArray: 5 }] : []),
        {
          y: symlog(0),
          yAxisIndex: 1,
          borderColor: 'gray',
          strokeDashArray: 5,
          label: {
            text: '0',
            position: 'right',
            offsetX: 6.5,
            offsetY: 5,
            textAnchor: 'middle',
            borderWidth: 0,
            borderRadius: 0,
            borderColor: 'transparent',
            style: {
              color: chartColors[1],
              fontWeight: 'bold',
              fontSize: '12',
            },
          },
        },
      ],
    },
    tooltip: {
      y: {
        formatter: (value: number) => f(symlog.invert(value), { decimal: 2 }) + '(USD)',
      },
      x: {
        show: true,
        formatter: (value, { dataPointIndex, w }) => {
          return m_categories[dataPointIndex] ?? value;
        },
      },
    },
  });
  const chartOptions = useChart({
    colors: chartColors,
    legend: { show: true },
    chart: {
      events: {
        legendClick: (chartContext, seriesIndex, opts) => {
          const containerElement = document.getElementById('revenue-chart-container');
          if (containerElement) {
            if (seriesIndex === 0) {
              if (containerElement.style.paddingLeft === '40px')
                containerElement.style.paddingLeft = '8px';
              else containerElement.style.paddingLeft = '40px';
            }
          }
        },
      },
    },
    xaxis: {
      categories: chart.categories,
      labels: {
        formatter: (value, timestamp: number, opts: number) => {
          if (m_categories.length > 30) {
            const index = (chart.categories || []).indexOf(value);
            return index % 7 === 0 ? moment(new Date(value).toString()).format('DD-MM-YY') : '';
          }
          return moment(new Date(value).toString()).format('DD-MM-YY');
        },
        rotate: -45,
        rotateAlways: true,
      },
    },
    yaxis: [
      {
        title: {
          text: `(USD)${translate('module.dashBoard.revenue')}`,
          style: { color: chartColors[0] },
        },
        labels: {
          formatter: (value: number) => fShortenNumber(value),
          style: {
            colors: chartColors[0],
          },
        },
        min: 0,
      },
      {
        opposite: true,
        title: {
          text: `${translate('module.dashBoard.profitOverTime')}(USD)`,
          style: { color: chartColors[1] },
        },
        labels: {
          formatter: (value: number) => fShortenNumber(value),
          style: {
            colors: chartColors[1],
          },
          offsetX: -10,
        },
        min: (min) => {
          if (min >= 0) return 0;
          // eslint-disable-next-line prefer-exponentiation-operator, no-restricted-properties
          const magnitude = Math.pow(10, Math.floor(Math.log10(-min)));
          const multiple = -min / magnitude; // example : 1.22
          const offset = multiple - Math.floor(multiple); // example : 0.22
          const buffer = offset < 0.5 ? 0.5 : 0;
          const bufferMagnitude = buffer * magnitude;
          const result = -(Math.round(multiple) * magnitude + bufferMagnitude);
          return result * (m_categories.length > 15 ? 1.5 : 1);
        },
      },
    ],
    annotations: {
      yaxis: [
        ...(hasNegativeProfit ? [{ y: symlog(0), borderColor: 'gray', strokeDashArray: 5 }] : []),
        {
          y: symlog(0),
          yAxisIndex: 1,
          borderColor: 'gray',
          strokeDashArray: 5,
          label: {
            text: '0',
            position: 'right',
            offsetX: 6.5,
            offsetY: 5,
            textAnchor: 'middle',
            borderWidth: 0,
            borderRadius: 0,
            borderColor: 'transparent',
            style: {
              color: chartColors[1],
              fontWeight: 'bold',
              fontSize: '12',
            },
          },
        },
      ],
    },
    tooltip: {
      y: {
        formatter: (value: number) => f(value, { decimal: 2 }) + '(USD)',
        title: {
          formatter: (seriesName: string) => {
            return seriesName.replace(/\s*\(.*?\)\s*/g, '');
          },
        },
      },
      x: {
        show: true,
        formatter: (value, { dataPointIndex, w }) => {
          return m_categories[dataPointIndex] ?? value;
        },
      },
    },
  });

  const filteredOptions = showLogScale ? logScaleChartOptions : chartOptions;

  const title =
    titleRevenue && titleProfit ? (
      <Stack flexDirection="row" alignItems="center">
        {titleRevenue}
        {tooltipRevenue && (
          <Tooltip
            title={<Typography fontSize={12}>{tooltipRevenue}</Typography>}
            placement="top"
            enterDelay={300}
          >
            <Iconify
              width={16}
              icon="eva:info-outline"
              ml="5px"
              sx={{ cursor: 'pointer', color: 'text.disabled' }}
            />
          </Tooltip>
        )}
        <Box marginX={1}>/</Box>
        {titleProfit}
        {tooltipProfit && (
          <Tooltip
            title={<Typography fontSize={12}>{tooltipProfit}</Typography>}
            placement="top"
            enterDelay={300}
          >
            <Iconify
              width={16}
              icon="eva:info-outline"
              ml="5px"
              sx={{ cursor: 'pointer', color: 'text.disabled' }}
            />
          </Tooltip>
        )}
      </Stack>
    ) : null;

  return (
    <Card {...other} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardHeader
        title={title}
        subheader={subheader}
        action={
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={showLogScale} onChange={handleToggleProfit} />}
              label={translate('module.dashBoard.chartMode')}
            />
          </Stack>
        }
      />
      <Chart
        ref={chartRef}
        id="revenue-chart-container"
        type="line"
        series={filteredSeries}
        options={filteredOptions}
        minHeight={'250px'}
        height="100%"
        sx={{
          mb: 2,
          pl: 1,
          mt: 0,
          pr: 2,
          flex: 1,
          overflow: 'visible',
          '.apexcharts-canvas > svg': { overflow: 'visible' },
        }}
      />
    </Card>
  );
}
